import { ForecastRevenueResponse, ForecastTableRow } from '../store/reducers/revenueForecastSlice';
import { 
  RevenueForecastResponse, 
  BusinessUnit, 
  Department, 
  Client, 
  QuarterData,
  TotalRevenue
} from '../types/revenueForecast';

/**
 * Transform the old flat API response structure to the new hierarchical structure
 * required by the Finance View component
 */
export function transformOldToNewStructure(
  oldData: ForecastRevenueResponse
): RevenueForecastResponse {
  console.log('Transformer - Input data:', oldData);
  console.log('Transformer - Table Data:', oldData['Table Data']);
  console.log('Transformer - Total keys:', Object.keys(oldData));
  console.log('Transformer - revenueForecast property:', (oldData as any).revenueForecast);
  
  // Check if data is already in the new format
  if ((oldData as any).revenueForecast) {
    console.log('Data already in new format, returning as-is');
    return oldData as any;
  }
  
  // Group data by BU -> Department -> Client
  const buMap = new Map<string, BusinessUnit>();

  // Process each row from the flat data structure
  const tableData = oldData['Table Data'] || [];
  console.log('Transformer - Processing rows:', tableData.length);
  
  tableData.forEach((row: ForecastTableRow) => {
    const buName = row.businessUnit || 'Unknown BU';
    const deptName = row.department || 'Unknown Department';
    const clientName = row.client || 'Unknown Client';

    // Get or create BU
    if (!buMap.has(buName)) {
      buMap.set(buName, {
        type: 'BU',
        name: buName,
        departments: [],
        quarters: [],
        totals: {
          projectRevenue: 0,
          pipelineRevenue: 0,
          total: 0,
          actualRevenue: 0
        }
      });
    }
    const bu = buMap.get(buName)!;

    // Get or create Department
    let dept = bu.departments.find((d) => d.name === deptName);
    if (!dept) {
      dept = {
        type: 'DEPT',
        name: deptName,
        clients: [],
        quarters: [],
        totals: {
          projectRevenue: 0,
          pipelineRevenue: 0,
          total: 0,
          actualRevenue: 0
        }
      };
      bu.departments.push(dept);
    }

    // Get or create Client
    let client = dept.clients?.find((c) => c.name === clientName);
    if (!client) {
      client = {
        type: 'CLIENT',
        name: clientName,
        quarters: [],
        totals: {
          projectRevenue: 0,
          pipelineRevenue: 0,
          total: 0,
          actualRevenue: 0
        }
      };
      if (!dept.clients) dept.clients = [];
      dept.clients.push(client);
    }

    // Create quarter data from the row
    const quarters: QuarterData[] = [];
    const projectQuarters = row.revenueFromProject?.quarters || {};
    const pipelineQuarters = row.revenueFromPipeline?.quarters || {};
    const actualQuarters = row.actual?.quarters || {};
    
    // Get all unique quarter names
    const allQuarterNames = new Set([
      ...Object.keys(projectQuarters),
      ...Object.keys(pipelineQuarters),
      ...Object.keys(actualQuarters)
    ]);

    // Create QuarterData for each quarter
    allQuarterNames.forEach((quarterName) => {
      const projectRev = projectQuarters[quarterName] || 0;
      const pipelineRev = pipelineQuarters[quarterName] || 0;
      const actualRev = actualQuarters[quarterName] || 0;
      
      quarters.push({
        quarter: quarterName,
        projectRevenue: projectRev,
        pipelineRevenue: pipelineRev,
        total: projectRev + pipelineRev,
        actualRevenue: actualRev,
        status: 'Past' // You can determine this based on current date/quarter
      });
    });

    // Add quarters to client and calculate totals
    client.quarters = quarters;
    client.totals = {
      projectRevenue: row.revenueFromProject?.total || 0,
      pipelineRevenue: row.revenueFromPipeline?.total || 0,
      total: (row.revenueFromProject?.total || 0) + (row.revenueFromPipeline?.total || 0),
      actualRevenue: row.actual?.total || 0
    };

    // Aggregate quarters at department level
    quarters.forEach((q) => {
      let deptQuarter = dept!.quarters.find((dq) => dq.quarter === q.quarter);
      if (!deptQuarter) {
        deptQuarter = {
          quarter: q.quarter,
          projectRevenue: 0,
          pipelineRevenue: 0,
          total: 0,
          actualRevenue: 0,
          status: q.status
        };
        dept!.quarters.push(deptQuarter);
      }
      deptQuarter.projectRevenue += q.projectRevenue;
      deptQuarter.pipelineRevenue += q.pipelineRevenue;
      deptQuarter.total += q.total;
      deptQuarter.actualRevenue += q.actualRevenue;
    });

    // Aggregate totals to department
    dept.totals.projectRevenue += client.totals.projectRevenue;
    dept.totals.pipelineRevenue += client.totals.pipelineRevenue;
    dept.totals.total += client.totals.total;
    dept.totals.actualRevenue += client.totals.actualRevenue;

    // Aggregate quarters at BU level
    quarters.forEach((q) => {
      let buQuarter = bu.quarters.find((bq) => bq.quarter === q.quarter);
      if (!buQuarter) {
        buQuarter = {
          quarter: q.quarter,
          projectRevenue: 0,
          pipelineRevenue: 0,
          total: 0,
          actualRevenue: 0,
          status: q.status
        };
        bu.quarters.push(buQuarter);
      }
      buQuarter.projectRevenue += q.projectRevenue;
      buQuarter.pipelineRevenue += q.pipelineRevenue;
      buQuarter.total += q.total;
      buQuarter.actualRevenue += q.actualRevenue;
    });

    // Aggregate totals to BU
    bu.totals.projectRevenue += client.totals.projectRevenue;
    bu.totals.pipelineRevenue += client.totals.pipelineRevenue;
    bu.totals.total += client.totals.total;
    bu.totals.actualRevenue += client.totals.actualRevenue;
  });

  // Convert map to array
  const businessUnits = Array.from(buMap.values());

  // Build total revenue from API totals
  const totalProjectRev = oldData['Total Forecasted Revenue from Projects'] || oldData['Total Forecasted Revenue (Projects + Pipeline)'];
  const totalPipelineRev = oldData['Total Forecasted Revenue from Pipeline'];
  const totalActualRev = oldData['Total Actual Revenue'];
  
  const totalQuarters: QuarterData[] = [];
  const projectQuarters = totalProjectRev?.quarters || {};
  const pipelineQuarters = totalPipelineRev?.quarters || {};
  const actualQuarters = totalActualRev?.quarters || {};

  // Get all unique quarter names
  const allQuarterNames = new Set([
    ...Object.keys(projectQuarters),
    ...Object.keys(pipelineQuarters),
    ...Object.keys(actualQuarters)
  ]);

  // Create QuarterData for total revenue
  allQuarterNames.forEach((quarterName) => {
    const projectRev = projectQuarters[quarterName] || 0;
    const pipelineRev = pipelineQuarters[quarterName] || 0;
    const actualRev = actualQuarters[quarterName] || 0;
    
    totalQuarters.push({
      quarter: quarterName,
      projectRevenue: projectRev,
      pipelineRevenue: pipelineRev,
      total: projectRev + pipelineRev,
      actualRevenue: actualRev,
      status: 'Past' // Determine based on current date
    });
  });

  const totalRevenue: TotalRevenue = {
    projects: totalProjectRev?.total || 0,
    pipeline: totalPipelineRev?.total || 0,
    total: (totalProjectRev?.total || 0) + (totalPipelineRev?.total || 0),
    actuals: totalActualRev?.total || 0,
    quarters: totalQuarters
  };

  return {
    revenueForecast: {
      businessUnits,
      TotalRevenue: totalRevenue
    },
    page: oldData.page || 0,
    size: oldData.size || 10,
    totalElements: oldData.totalElements || 0,
    totalPages: oldData.totalPages || 1,
    last: oldData.last || false
  };
}
